// src/components/agenda/PollDisplay.js
import React, { useState, useEffect, useMemo } from 'react';
import { db, functions } from '../../services/firebase'; 
import { doc, onSnapshot, collection, updateDoc, query, orderBy } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import ConfirmationModal from '../ConfirmationModal';
import PollForm from './PollForm';

// Helper für Cloud Function-Aufrufe
const startPollFunc = httpsCallable(functions, 'startPoll');
const endPollFunc = httpsCallable(functions, 'endPoll');
const castVoteFunc = httpsCallable(functions, 'castVote');

// --- UNTERKOMPONENTEN ---

const PollResults = ({ results }) => {
    const totalVotes = useMemo(() => Object.values(results || {}).reduce((sum, count) => sum + count, 0), [results]);
    const sortedResults = useMemo(() => Object.entries(results || {}).sort(([, countA], [, countB]) => countB - countA), [results]);
    return (
        <div className="space-y-2">
            <h5 className="text-sm font-semibold text-gray-600 dark:text-gray-400 mb-2">Umfrage beendet. Finales Ergebnis:</h5>
            {sortedResults.map(([optionText, count]) => {
                const percentage = totalVotes > 0 ? (count / totalVotes) * 100 : 0;
                const isAbstention = optionText.toLowerCase() === 'enthaltung';
                return (
                    <div key={optionText} className={`p-2 rounded-lg ${isAbstention ? 'bg-gray-100 dark:bg-gray-700' : 'bg-blue-50 dark:bg-blue-900/50'}`}>
                        <div className="flex justify-between items-center mb-1">
                            <span className={`font-semibold ${isAbstention ? 'text-gray-700 dark:text-gray-300' : 'text-blue-800 dark:text-blue-300'}`}>{optionText}</span>
                            <span className={`font-bold ${isAbstention ? 'text-gray-800 dark:text-gray-200' : 'text-blue-900 dark:text-blue-200'}`}>{count} {count === 1 ? 'Stimme' : 'Stimmen'}</span>
                        </div>
                        <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2.5">
                            <div className={`h-2.5 rounded-full ${isAbstention ? 'bg-gray-400' : 'bg-blue-500'}`} style={{ width: `${percentage}%` }}></div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

const PollModeratorView = ({ poll, companyId, meetingId, totalPresentParticipants, setNotification, onEdit, hasPresentGuests }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [actionType, setActionType] = useState(null);
    const [liveAuthVoteCount, setLiveAuthVoteCount] = useState(0);
    const [liveGuestVoteCount, setLiveGuestVoteCount] = useState(0); 

    useEffect(() => {
        if (!poll || poll.status !== 'open') return;
        const votesColRef = collection(db, `companies/${companyId}/meetings/${meetingId}/polls/${poll.id}/votes`);
        const unsubAuth = onSnapshot(votesColRef,
            (snap) => setLiveAuthVoteCount(snap.size),
            (err) => console.error("Auth vote count error:", err)
        );
        return () => unsubAuth();
    }, [poll?.status, companyId, meetingId, poll?.id]);

    const handleAction = async () => {
        setIsLoading(true); setShowConfirm(false);
        const data = { companyId, meetingId, pollId: poll.id };
        try {
            if (actionType === 'start') await startPollFunc(data);
            else if (actionType === 'end') await endPollFunc(data);
            setNotification({ message: `Umfrage ${actionType === 'start' ? 'gestartet' : 'beendet'}!`, type: 'success' });
        } catch (error) { console.error(`Error ${actionType} poll:`, error); setNotification({ message: `Aktion fehlgeschlagen: ${error.message}`, type: 'error' }); }
        finally { setIsLoading(false); setActionType(null); }
    };

    const totalLiveVotes = liveAuthVoteCount + liveGuestVoteCount;

    if (poll.status === 'pending') {
        return (
            <div className="flex gap-2 mt-2">
                <button
                    onClick={onEdit}
                    disabled={isLoading}
                    className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-200 rounded-lg font-medium hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors duration-200 disabled:opacity-50"
                >
                    Bearbeiten
                </button>
                <button
                    onClick={() => { setActionType('start'); setShowConfirm(true); }}
                    disabled={isLoading}
                    className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors duration-200 disabled:opacity-50"
                >
                    Starten
                </button>
                <ConfirmationModal
                    isOpen={showConfirm && actionType === 'start'}
                    title="Umfrage starten?"
                    message="Möchten Sie diese Umfrage jetzt für alle anwesenden Teilnehmer starten?"
                    onConfirm={handleAction}
                    onCancel={() => setShowConfirm(false)}
                    confirmText="Starten"
                    cancelText="Nein"
                    confirmColor="bg-green-600 hover:bg-green-700"
                />
            </div>
        );
    }

    if (poll.status === 'open') {
        return (
            <div className="text-center mt-2 space-y-4">
                <div className="mb-3 p-3 bg-blue-100 dark:bg-blue-900 rounded-lg">
                    <span className="font-bold text-blue-800 dark:text-blue-200 text-lg">Abstimmung läuft...</span>
                    <p className="text-2xl font-extrabold text-blue-900 dark:text-blue-100 mt-1">{totalLiveVotes} / {totalPresentParticipants}</p>
                    <p className="text-sm text-blue-700 dark:text-blue-300">Stimmen abgegeben (Anwesende)</p>
                </div>
                <button
                    onClick={() => { setActionType('end'); setShowConfirm(true); }}
                    disabled={isLoading}
                    className="w-full px-4 py-3 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors duration-200 disabled:opacity-50"
                >
                    Abstimmung beenden
                </button>
                <ConfirmationModal
                    isOpen={showConfirm && actionType === 'end'}
                    title="Umfrage beenden?"
                    message="Möchten Sie die Abstimmung jetzt beenden? Das Ergebnis wird finalisiert."
                    onConfirm={handleAction}
                    onCancel={() => setShowConfirm(false)}
                    confirmText="Ja, beenden"
                    cancelText="Nein"
                    confirmColor="bg-red-600 hover:bg-red-700"
                />
            </div>
        );
    }

    return null;
};

const PollParticipantView = ({ poll, companyId, meetingId, currentUserId, setNotification, isParticipantPresent }) => {
    const [hasVoted, setHasVoted] = useState(null);
    // NEU: Zeigt an, welche Option gerade gesendet wird (für visuelles Feedback)
    const [votingForId, setVotingForId] = useState(null); 

    useEffect(() => {
        if (!poll || poll.status !== 'open') { setHasVoted(false); return; }
        setHasVoted(null);
        const voteDocRef = doc(db, `companies/${companyId}/meetings/${meetingId}/polls/${poll.id}/votes/${currentUserId}`);
        const unsubscribe = onSnapshot(voteDocRef,
            (docSnapshot) => setHasVoted(docSnapshot.exists()),
            (error) => { console.error("Vote listener error:", error); setNotification({ message: "Fehler: Abstimmungsstatus-Prüfung fehlgeschlagen.", type: 'error' }); }
        );
        return () => unsubscribe();
     }, [companyId, meetingId, poll?.id, currentUserId, poll?.status, setNotification]);

    const handleVote = async (optionId) => {
        if (!optionId && optionId !== 'abstain') return;
        
        setVotingForId(optionId); // UI sperren und Spinner anzeigen
        try {
            await castVoteFunc({ companyId, meetingId, pollId: poll.id, optionId: optionId });
            setNotification({ message: 'Stimme erfolgreich abgegeben!', type: 'success' });
            // Erfolgreich: votingForId bleibt gesetzt, bis die Komponente durch hasVoted=true neu rendert
        }
        catch (error) { 
            console.error("Vote error:", error); 
            setNotification({ message: `Abstimmung fehlgeschlagen: ${error.message}`, type: 'error' }); 
            setVotingForId(null); // Bei Fehler UI wieder freigeben
        }
    };

    if (hasVoted === null && poll.status === 'open') {
        return <div className="text-center p-4 text-gray-500">Prüfe Abstimmungsstatus...</div>;
    }
    if (hasVoted) {
        return (
            <div className="text-center p-4 bg-green-100 dark:bg-green-900 rounded-lg mt-2 animate-fadeIn">
                <span className="font-semibold text-green-800 dark:text-green-200">Vielen Dank für Ihre Teilnahme.</span>
            </div>
        );
    }
    if (poll.status !== 'open') { return null; }

    if (!isParticipantPresent) {
        return (
             <div className="text-center p-3 bg-yellow-100 dark:bg-yellow-900 rounded-lg mt-2">
                <span className="text-yellow-800 dark:text-yellow-200 font-medium">Nur anwesende Teilnehmer können abstimmen.</span>
            </div>
        );
    }

    return (
        <div className="space-y-2 mt-2">
            <h5 className="text-sm font-semibold text-gray-600 dark:text-gray-400 mb-2">Bitte abstimmen:</h5>
            {poll.options.map(option => (
                <button
                    key={option.id}
                    onClick={() => handleVote(option.id)}
                    disabled={votingForId !== null} // Alle Buttons deaktivieren, wenn abgestimmt wird
                    className={`w-full text-left px-4 py-3 border rounded-lg font-semibold transition-all duration-200 
                        ${votingForId === option.id 
                            ? 'bg-blue-100 border-blue-500 text-blue-800 ring-2 ring-blue-300' 
                            : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-600'
                        }
                        ${votingForId !== null && votingForId !== option.id ? 'opacity-50 cursor-not-allowed' : 'transform hover:scale-[1.02]'}
                    `}
                >
                    {option.text} {votingForId === option.id && <span className="ml-2">⏳</span>}
                </button>
            ))}
            <button
                key="abstain"
                onClick={() => handleVote('abstain')}
                disabled={votingForId !== null}
                className={`w-full text-left px-4 py-3 border border-gray-300 dark:border-gray-500 rounded-lg font-medium transition-all duration-200
                    ${votingForId === 'abstain'
                        ? 'bg-gray-200 dark:bg-gray-500 ring-2 ring-gray-400'
                        : 'bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-500'
                    }
                    ${votingForId !== null && votingForId !== 'abstain' ? 'opacity-50 cursor-not-allowed' : ''}
                `}
            >
                Enthaltung {votingForId === 'abstain' && <span className="ml-2">⏳</span>}
            </button>
        </div>
    );
};

const PollDisplay = ({ poll, companyId, meetingId, currentUserId, isModerator, totalParticipants, setNotification, allParticipants = [] }) => {

    const [isEditing, setIsEditing] = useState(false);

    // === HOOKS VOR EARLY RETURNS ===
    
    const currentUserParticipant = useMemo(() =>
        allParticipants.find(p => p.uid === currentUserId)
    , [allParticipants, currentUserId]);

    const isCurrentUserPresent = useMemo(() => {
        const status = currentUserParticipant?.currentStatus;
        return status === 'present' || status === 'joined_late' || (isModerator && !currentUserParticipant);
    }, [currentUserParticipant, isModerator]);

    const presentParticipantsCount = useMemo(() =>
        allParticipants.filter(p => (p.currentStatus === 'present' || p.currentStatus === 'joined_late') && !p.isExternal).length
    , [allParticipants]);

    const hasPresentGuests = useMemo(() =>
        allParticipants.some(p => (p.currentStatus === 'present' || p.currentStatus === 'joined_late') && p.isExternal)
    , [allParticipants]);


    // === SAFETY CHECK ===
    if (!poll) return null;


    const handleUpdatePoll = async (updatedPollData) => {
        const pollRef = doc(db, `companies/${companyId}/meetings/${meetingId}/polls/${poll.id}`);
        try {
            await updateDoc(pollRef, {
                question: updatedPollData.question,
                options: updatedPollData.options
            });
            setNotification({ message: 'Umfrage erfolgreich aktualisiert!', type: 'success' });
            setIsEditing(false);
        }
        catch (error) {
            console.error("Error updating poll:", error);
            setNotification({ message: `Update fehlgeschlagen: ${error.message}`, type: 'error' });
        }
     };

    if (isEditing) {
        return (
            <PollForm
                existingPoll={poll}
                onSave={handleUpdatePoll} 
                onCancel={() => setIsEditing(false)}
                setNotification={setNotification}
                companyId={companyId}
                meetingId={meetingId}
                agendaItemId={poll.agendaItemId} 
                currentUserId={currentUserId}
            />
        );
    }

    return (
        <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 dark:from-gray-800 dark:to-gray-900 p-4 rounded-2xl shadow-lg mb-5 border border-yellow-200 dark:border-yellow-700">
            <h5 className="text-lg font-bold text-gray-900 dark:text-white mb-3">{poll.question}</h5>

            {poll.status === 'pending' && (
                 <div className="pl-4 mt-2 mb-3">
                     <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-1">
                        {poll.options.map(opt => <li key={opt.id}>{opt.text}</li>)}
                     </ul>
                     <div className="text-center p-3 bg-gray-100 dark:bg-gray-700 rounded-lg mt-3">
                        <span className="font-medium text-gray-700 dark:text-gray-300">Wartet auf Start durch Moderator...</span>
                     </div>
                 </div>
             )}

            {poll.status === 'published' && ( <PollResults results={poll.results} /> )}

            {isModerator && poll.status !== 'published' && (
                <PollModeratorView
                    poll={poll}
                    companyId={companyId}
                    meetingId={meetingId}
                    totalPresentParticipants={presentParticipantsCount}
                    setNotification={setNotification}
                    onEdit={() => setIsEditing(true)}
                    hasPresentGuests={hasPresentGuests}
                />
            )}

            {poll.status !== 'published' && (
                <PollParticipantView
                    poll={poll}
                    companyId={companyId}
                    meetingId={meetingId}
                    currentUserId={currentUserId}
                    setNotification={setNotification}
                    isParticipantPresent={isCurrentUserPresent}
                />
            )}

            <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
                Erstellt von: {poll.createdByInfo?.name || 'Unbekannt'}
            </p>
        </div>
    );
};

export default PollDisplay;